import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  ActiveCallOverlayCloseEvent,
  ActiveCallOverlayCloseReason,
  ActiveCallOverlayVM,
  ActiveCallOverlayOpenVM,
  ActiveCallOverlaySaveEvent,
  ActiveCallOverlayState
} from '../components/active-call-overlay/active-call-overlay.models';
import { AuthService } from './auth.service';
import { LocalizationService } from './localization.service';

@Injectable({
  providedIn: 'root'
})
export class ActiveCallOverlayService {
  private readonly closedState: ActiveCallOverlayState = {
    isOpen: false,
    isSaving: false,
    data: null
  };

  private readonly stateSubject = new BehaviorSubject<ActiveCallOverlayState>(this.closedState);
  private readonly saveRequestedSubject = new Subject<ActiveCallOverlaySaveEvent>();
  private readonly closedSubject = new Subject<ActiveCallOverlayCloseEvent>();

  public readonly state$: Observable<ActiveCallOverlayState> = this.stateSubject.asObservable();
  public readonly saveRequested$: Observable<ActiveCallOverlaySaveEvent> = this.saveRequestedSubject.asObservable();
  public readonly closed$: Observable<ActiveCallOverlayCloseEvent> = this.closedSubject.asObservable();

  constructor(
    private ls: LocalizationService,
    authService: AuthService
  ) {
    authService.logout$.subscribe(() => this.close('system'));
  }

  public get snapshot(): ActiveCallOverlayState {
    return this.stateSubject.value;
  }

  public open(data: ActiveCallOverlayOpenVM): void {
    this.stateSubject.next({
      isOpen: true,
      isSaving: false,
      data: this.normalizeData(data)
    });
  }

  public patch(data: Partial<ActiveCallOverlayOpenVM>): void {
    const current = this.stateSubject.value;

    if (!current.data) return;

    this.stateSubject.next({ ...current, data: this.normalizeData({ ...current.data, ...data }) });
  }

  public setSaving(isSaving: boolean): void {
    const current = this.stateSubject.value;

    if (!current.isOpen) return;

    this.stateSubject.next({ ...current, isSaving });
  }

  public requestSave(note: string): void {
    const current = this.stateSubject.value;

    if (!current.data) return;

    const normalizedNote = note ?? '';
    const data = { ...current.data, note: normalizedNote };

    this.stateSubject.next({ ...current, data });
    this.saveRequestedSubject.next({ note: normalizedNote, data });
  }

  public close(reason: ActiveCallOverlayCloseReason = 'system'): void {
    const current = this.stateSubject.value;

    if (!current.isOpen || !current.data) return;

    const data = current.data;

    this.stateSubject.next(this.closedState);
    this.closedSubject.next({ reason, data });
  }

  private normalizeData(data: ActiveCallOverlayOpenVM): ActiveCallOverlayVM {
    const defaultData = this.getDefaultData();

    return {
      ...defaultData,
      ...data,
      id: data.id ?? defaultData.id,
      title: data.title?.trim() || defaultData.title,
      note: data.note ?? defaultData.note,
      callIcon: data.callIcon?.trim() || defaultData.callIcon,
      noteLabel: data.noteLabel?.trim() || defaultData.noteLabel,
      notePlaceholder: data.notePlaceholder?.trim() || defaultData.notePlaceholder,
      saveButtonLabel: data.saveButtonLabel?.trim() || defaultData.saveButtonLabel,
      closeButtonLabel: data.closeButtonLabel?.trim() || defaultData.closeButtonLabel
    };
  }

  private getDefaultData(): Omit<ActiveCallOverlayVM, 'contactName' | 'phoneNumber'> {
    return {
      id: null,
      title: this.ls.translate('connection.activeCallTitle'),
      note: '',
      callIcon: 'call',
      noteLabel: this.ls.translate('connection.noteLabel'),
      notePlaceholder: this.ls.translate('placeholder.noteContent'),
      saveButtonLabel: this.ls.translate('button.save'),
      closeButtonLabel: this.ls.translate('button.close')
    };
  }
}
